import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import { pageHintFromPath } from "../lib/assistantPageContext";
import {
  ASSISTANT_RESULT,
  MAX_ASSISTANT_MESSAGE_CHARS,
  sendAssistantMessage,
} from "../lib/assistantClient";
import AssistantPanel from "../components/assistant/AssistantPanel";

/**
 * Phase 4A.5A — the ONE owner of Assistant state, and the only place a turn is
 * dispatched or applied.
 *
 * **Why this is a provider and not the launcher.** Every page renders its own
 * `<Layout>`, so a launcher that owned the conversation lost it on ordinary
 * navigation — and, worse, an in-flight request that outlived a tenant switch
 * had nothing left to check itself against. This provider is mounted once,
 * above `<Routes>`, so both the transcript and the request lifecycle survive a
 * route change while remaining strictly per-session.
 *
 * **But only within the tenant application.** Mounted above the complete route
 * tree, the provider also sees /login, /auth/*, /q/:token and /platform-admin.
 * The Assistant belongs to none of them, so a closed allowlist — the page-hint
 * route table, which is exactly the set <Guarded> protects — gates both the
 * portal and the session: leaving the tenant application ends the conversation
 * the same way an organization switch does.
 *
 * **Still nothing is persisted.** All state is React state and refs inside this
 * component: no localStorage, no sessionStorage, no IndexedDB, no cookie, no
 * cache, no module-global, no backend row. A reload starts empty because there
 * is nowhere for a conversation to have been written.
 *
 * **Every applied result must still belong to the session that asked.** Clearing
 * state is not enough: an already-issued Promise resolves regardless. A turn
 * therefore carries the generation, the request identity and the session
 * identity it was dispatched under, and is dropped in silence unless all three
 * still match at the moment its answer arrives. Ending a session bumps the
 * generation FIRST and aborts second, so even a synchronous rejection is
 * already stale when it lands.
 */

const AssistantCtx = createContext(null);

/** `null` when no provider is mounted, so a stray consumer renders nothing. */
export function useAssistant() {
  return useContext(AssistantCtx);
}

/** The element made inert while the dialog is open. Restored exactly. */
const APP_ROOT_ID = "root";

export function AssistantProvider({ children }) {
  const { t } = useTranslation();
  const { orgId, user } = useAuth();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState([]);
  const [draft, setDraftState] = useState("");
  const [pending, setPending] = useState(false);
  const [inputError, setInputError] = useState("");

  // ---------------------------------------------------------------------
  // The tenant application, as a CLOSED allowlist.
  //
  // `pageHintFromPath` already names every route <Guarded> protects in App.js
  // and nothing else, so "this path has a page hint" is exactly "this path is
  // an authenticated tenant page". Reusing it keeps one route list instead of
  // two that can drift, and it fails closed: /login, /register, /auth/*,
  // /q/:token, /platform-admin, an unmapped path and a future route all
  // produce no hint, so the Assistant does not exist on any of them.
  //
  // This matters because the provider is mounted above the COMPLETE <Routes>
  // tree and the dialog is portalled to document.body: without this gate the
  // panel outlived the route it was opened from, leaving a private tenant
  // transcript on a customer-facing quotation page and holding #root inert
  // over a login or password-reset form.
  // ---------------------------------------------------------------------
  const onTenantRoute = pageHintFromPath(location.pathname) !== null;

  // ---------------------------------------------------------------------
  // Identity of the current signed-in session. A conversation belongs to one
  // user in one organization; anything else is a different session.
  // ---------------------------------------------------------------------
  const sessionKey = `${user?.id || ""}::${orgId || ""}`;
  const sessionRef = useRef(null);

  // Lifecycle generation. Bumped whenever the conversation stops being the one
  // an outstanding request was asked in.
  const genRef = useRef(0);
  // The single request allowed to be in flight, or null.
  const activeRef = useRef(null);
  const reqSeqRef = useRef(0);
  // React keys only — a ref, so not even an integer outlives the provider.
  const turnSeqRef = useRef(0);
  const mountedRef = useRef(false);
  // Read at dispatch time, so a turn always carries the route it was sent from.
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  // The draft, mirrored so `submit` keeps a stable identity across keystrokes.
  const draftRef = useRef("");
  const launcherRef = useRef(null);
  const restoreFocusRef = useRef(false);
  // Compared, never assumed — the same reason sessionRef is.
  const tenantRouteRef = useRef(onTenantRoute);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const nextTurnId = useCallback(() => {
    turnSeqRef.current += 1;
    return `t${turnSeqRef.current}`;
  }, []);

  const setDraft = useCallback((value) => {
    draftRef.current = value;
    setDraftState(value);
  }, []);

  /**
   * Retire the current generation and abort whatever it had in flight.
   *
   * Order matters: the generation moves and the slot empties BEFORE the abort,
   * so a handler that runs synchronously off `abort()` already fails the
   * staleness check rather than racing it.
   */
  const invalidate = useCallback(() => {
    genRef.current += 1;
    const active = activeRef.current;
    activeRef.current = null;
    if (active) {
      try {
        active.controller.abort();
      } catch {
        // An AbortController that refuses to abort still cannot deliver a
        // result: the generation above already disowned this request.
      }
    }
  }, []);

  /** "Нов разговор" — and the shared body of every session boundary. */
  const reset = useCallback(() => {
    invalidate();
    setTurns([]);
    setDraft("");
    setInputError("");
    setPending(false);
  }, [invalidate, setDraft]);

  /** A boundary the conversation may not cross: close it and empty it. */
  const endSession = useCallback(() => {
    // Focus is inside a dialog that is about to disappear. Send it back to the
    // launcher rather than letting it fall to the document body — unless the
    // launcher went too, which is what logout does, and which the restore
    // guards against by checking the node is still connected.
    restoreFocusRef.current = true;
    setOpen(false);
    reset();
  }, [reset]);

  // The tenant / identity boundary. Recorded in a layout effect so the marker
  // is committed before any Promise continuation can observe it, and compared
  // rather than assumed so StrictMode's re-invocation clears nothing.
  useLayoutEffect(() => {
    if (sessionRef.current === sessionKey) return;
    const first = sessionRef.current === null;
    sessionRef.current = sessionKey;
    if (!first) endSession();
  }, [sessionKey, endSession]);

  // Leaving the tenant application is a session boundary in its own right: the
  // conversation, the draft and any turn in flight belong to the authenticated
  // shell and may not follow the user onto a public, auth or platform route.
  // Layout-timed like the identity boundary above, so the generation has moved
  // and the request is aborted before any Promise continuation can observe it.
  // Navigating BETWEEN two tenant routes never enters this branch.
  useLayoutEffect(() => {
    if (tenantRouteRef.current === onTenantRoute) return;
    tenantRouteRef.current = onTenantRoute;
    if (!onTenantRoute) endSession();
  }, [onTenantRoute, endSession]);

  // `switchOrg` dispatches this synchronously, before React re-renders. It is
  // the earliest possible signal, so it closes the window a state-derived
  // effect alone would leave open.
  useEffect(() => {
    window.addEventListener("quowada:orgChanged", endSession);
    return () => window.removeEventListener("quowada:orgChanged", endSession);
  }, [endSession]);

  // The provider going away is a session boundary too — nothing may resolve
  // into a tree that no longer exists.
  useEffect(() => () => invalidate(), [invalidate]);

  /** All three identities must still hold before a result may be applied. */
  const isCurrent = useCallback(
    (req) =>
      mountedRef.current &&
      activeRef.current === req &&
      req.generation === genRef.current &&
      req.sessionKey === sessionRef.current,
    [],
  );

  const submit = useCallback(async () => {
    // Synchronous, ref-based: two clicks in one React batch both read the same
    // stale `pending`, and the second must not reach the network.
    if (activeRef.current) return;

    const message = draftRef.current;
    // Counted in code points, exactly as the backend's `len()` does, so the UI
    // and the server agree on where 1500 is. The server stays authoritative.
    const used = Array.from(message).length;
    if (!message.trim()) {
      setInputError(t("assistant.empty_message"));
      return;
    }
    if (used > MAX_ASSISTANT_MESSAGE_CHARS) {
      setInputError(t("assistant.too_long", { max: MAX_ASSISTANT_MESSAGE_CHARS }));
      return;
    }

    reqSeqRef.current += 1;
    const req = {
      id: reqSeqRef.current,
      generation: genRef.current,
      sessionKey: sessionRef.current,
      controller: new AbortController(),
    };
    activeRef.current = req;

    setInputError("");
    setPending(true);
    // The question is shown exactly as it was written — never trimmed,
    // reformatted or rewritten, which is also how it goes on the wire.
    setTurns((prev) => [...prev, { id: nextTurnId(), author: "user", text: message }]);
    setDraft("");

    // Derived from the router's path at DISPATCH time, never from page content
    // or user input, and re-validated by the backend regardless.
    const pageHint = pageHintFromPath(pathnameRef.current);

    const { result, text } = await sendAssistantMessage({
      message,
      pageHint,
      signal: req.controller.signal,
    });

    // A stale turn changes nothing at all: no message, no error, and above all
    // no touching a newer request's pending state.
    if (!isCurrent(req)) return;
    activeRef.current = null;
    setPending(false);
    // Aborting is how a session ends, and an ended session has no reader.
    if (result === ASSISTANT_RESULT.ABORTED) return;
    setTurns((prev) => [
      ...prev,
      {
        id: nextTurnId(),
        author: "assistant",
        result,
        text: result === ASSISTANT_RESULT.ANSWER ? text : null,
      },
    ]);
  }, [isCurrent, nextTurnId, setDraft, t]);

  const onDraftChange = useCallback(
    (value) => {
      setDraft(value);
      setInputError("");
    },
    [setDraft],
  );

  // ---------------------------------------------------------------------
  // The launcher lives inside <Layout>, so navigation replaces it. It hands us
  // its node; focus returns to whichever one is currently on the page.
  // ---------------------------------------------------------------------
  const registerLauncher = useCallback((node) => {
    if (node) launcherRef.current = node;
    else if (launcherRef.current && !launcherRef.current.isConnected) {
      launcherRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    restoreFocusRef.current = true;
    setOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setOpen((wasOpen) => {
      if (wasOpen) restoreFocusRef.current = true;
      return !wasOpen;
    });
  }, []);

  // The dialog exists only when it is open AND the user is on a tenant route.
  // Derived during render, so the route gate closes in the same commit as the
  // navigation — before the layout effect above has even run.
  const dialogOpen = open && onTenantRoute;

  // While the dialog is open the rest of the application is inert, so neither
  // Tab nor a screen reader's browse mode can reach behind an aria-modal
  // dialog. The previous state is captured and restored exactly, and the
  // cleanup runs on close AND on unmount — logout can never strand it.
  useEffect(() => {
    if (!dialogOpen) return undefined;
    const appRoot = document.getElementById(APP_ROOT_ID);
    if (!appRoot) return undefined;
    const hadInert = appRoot.hasAttribute("inert");
    const previousAriaHidden = appRoot.getAttribute("aria-hidden");
    appRoot.setAttribute("inert", "");
    appRoot.setAttribute("aria-hidden", "true");
    return () => {
      if (!hadInert) appRoot.removeAttribute("inert");
      if (previousAriaHidden === null) appRoot.removeAttribute("aria-hidden");
      else appRoot.setAttribute("aria-hidden", previousAriaHidden);
    };
  }, [dialogOpen]);

  // Declared after the inert effect so its cleanup has already run: focusing a
  // button that is still inside an inert subtree does nothing. The connected
  // check is what keeps a route change from focusing a launcher that left with
  // the page — on a non-tenant route there is no launcher at all.
  useEffect(() => {
    if (dialogOpen || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    if (launcherRef.current?.isConnected) launcherRef.current.focus();
  }, [dialogOpen]);

  const value = useMemo(
    () => ({ open, toggle, close, registerLauncher }),
    [open, toggle, close, registerLauncher],
  );

  return (
    <AssistantCtx.Provider value={value}>
      {children}
      {/* Portalled out of the route subtree so it survives navigation between
          tenant pages and sits outside the element it makes inert — and gated
          on the tenant route so it survives nothing else. */}
      {dialogOpen &&
        createPortal(
          <AssistantPanel
            turns={turns}
            draft={draft}
            pending={pending}
            inputError={inputError}
            onDraftChange={onDraftChange}
            onSubmit={submit}
            onReset={reset}
            onClose={close}
          />,
          document.body,
        )}
    </AssistantCtx.Provider>
  );
}

export default AssistantProvider;
